import { type ReactNode, useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react'
import { Combobox as ComboboxPrimitive } from '@base-ui/react'
import { ChevronDownIcon, CirclePlusIcon, LoaderCircleIcon } from 'lucide-react'
import { useTranslation } from 'react-i18next'

import { cn } from '@/components/new/lib'
import { Badge, Button } from '@/components/new/ui'

import { ComboboxField } from './ComboboxField'
import { ValueSelectPresentationContext } from './presentationContext'
import {
    areFilterSelectorValuesEqual,
    isCloseByEscape,
    normalizeFilterSelectorValues,
    resolveFallbackSelectedOptions,
    toFilterSelectorValue,
} from './helpers'
import { useResolvedComboboxFieldSelectedOptions } from './selection-context'

import type { ComboboxFieldOption, ValueSelectSelectorProps, ValueSelectorButtonProps } from './types'

type ValueSelectorTriggerProps = {
    multiple: boolean
    isAsyncMode: boolean
    draftValues: string[]
    fallbackSelectedOptions: ComboboxFieldOption[]
    resolvedRenderButtonValue: (options: ComboboxFieldOption[]) => ReactNode
    shouldRenderEmptyStateButton: boolean
    hasCustomButtonContent: boolean
    noValueLabel: ReactNode
    children: ReactNode
    label: ReactNode
    shouldShowLabelDivider: boolean
    defaultValueClassName: string
    buttonProps: ValueSelectorButtonProps
    disabled: boolean | undefined
    showChevron: boolean
    invalid?: boolean
}

function ValueSelectorTrigger({
    multiple,
    isAsyncMode,
    draftValues,
    fallbackSelectedOptions,
    resolvedRenderButtonValue,
    shouldRenderEmptyStateButton,
    hasCustomButtonContent,
    noValueLabel,
    children,
    label,
    shouldShowLabelDivider,
    defaultValueClassName,
    buttonProps,
    disabled,
    showChevron,
    invalid,
}: ValueSelectorTriggerProps) {
    const { t } = useTranslation()
    const { selectedOptions: optionsForButton, isHydratingValue } = useResolvedComboboxFieldSelectedOptions({
        isAsyncMode,
        selectedValueCount: draftValues.length,
        fallbackSelectedOptions,
    })

    const isLoading = isAsyncMode && isHydratingValue
    const isDisabled = !!disabled || !!buttonProps.disabled || isLoading
    const loadingLabel = t('components.control.combobox.loading')

    const selectedButtonValue = useMemo(
        () => resolvedRenderButtonValue(optionsForButton),
        [optionsForButton, resolvedRenderButtonValue]
    )

    const resolvedButtonContent = shouldRenderEmptyStateButton ? (
        <span className="inline-flex min-w-0 items-center gap-1.5">
            <CirclePlusIcon className="size-4 shrink-0" data-icon="inline-start" aria-hidden />
            {noValueLabel && <span className="min-w-0 truncate">{noValueLabel}</span>}
        </span>
    ) : hasCustomButtonContent ? (
        children
    ) : isLoading ? (
        <span className="inline-flex min-w-0 items-center gap-1.5">
            <LoaderCircleIcon className="size-4 animate-spin" aria-hidden />
            <span className="min-w-0 truncate">{loadingLabel}</span>
        </span>
    ) : label ? (
        <span
            className={cn('inline-flex min-w-0 items-center gap-1.5', multiple && 'max-md:flex-col max-md:items-start')}
        >
            <span className={cn('text-muted-foreground shrink-0 font-normal', isAsyncMode && 'inline-flex items-center')}>
                {label}
            </span>
            {shouldShowLabelDivider && <span className={cn('bg-border h-4 w-px shrink-0', multiple && 'max-md:hidden')} />}
            <span className={cn(defaultValueClassName)}>{selectedButtonValue}</span>
        </span>
    ) : (
        <span className={cn(defaultValueClassName)}>{selectedButtonValue}</span>
    )

    const resolvedButtonClassName = cn(
        'justify-between w-fit max-w-full whitespace-normal px-2',
        multiple &&
            !hasCustomButtonContent &&
            !shouldRenderEmptyStateButton &&
            'max-md:h-auto max-md:min-h-9 max-md:py-2',
        shouldRenderEmptyStateButton && 'text-muted-foreground font-normal border-dashed',
        buttonProps.className
    )

    return (
        <ComboboxPrimitive.Trigger
            data-slot="combobox-trigger-button"
            render={
                <Button
                    variant="secondary"
                    {...buttonProps}
                    aria-invalid={invalid || undefined}
                    className={resolvedButtonClassName}
                    disabled={isDisabled}
                />
            }
            className="w-fit max-w-full"
            disabled={isDisabled}
        >
            {resolvedButtonContent}
            {showChevron && (
                <ChevronDownIcon className="text-muted-foreground size-4 shrink-0" data-icon="inline-end" />
            )}
        </ComboboxPrimitive.Trigger>
    )
}

export const ValueSelector = ({
    open: openProp,
    onOpenChange,
    triggerComponent: customTriggerComponent,
    value,
    onChange,
    options,
    asyncOptions,
    buildAsyncOptions,
    multiple = false,
    placeholder,
    searchPlaceholder,
    label,
    showLabelDivider,
    queryOnOpen = true,
    clearActionLabel,
    actions,
    normalizeCaseInsensitive = true,
    renderButtonValue,
    showChevron = false,
    buttonProps,
    children,
    ...comboboxProps
}: ValueSelectSelectorProps) => {
    const { t } = useTranslation()
    const { contentProps: contentPropsProp, listProps: listPropsProp, ...restComboboxProps } = comboboxProps
    const invalid = !!comboboxProps['aria-invalid']
    const normalizedIncomingValues = useMemo(
        () => normalizeFilterSelectorValues({ value, multiple, normalizeCaseInsensitive }),
        [multiple, normalizeCaseInsensitive, value]
    )
    const presentation = useContext(ValueSelectPresentationContext)
    const [localOpen, setLocalOpen] = useState(false)
    const isOpen = openProp ?? presentation?.open ?? localOpen
    const setIsOpen = useCallback(
        (open: boolean, eventDetails?: ComboboxPrimitive.Root.ChangeEventDetails) => {
            presentation?.onOpenChange(open, eventDetails)
            onOpenChange?.(open, eventDetails)
            if (eventDetails?.isCanceled) {
                return
            }

            setLocalOpen(open)
        },
        [onOpenChange, presentation]
    )
    const [draftValues, setDraftValues] = useState(normalizedIncomingValues)
    const draftValuesRef = useRef(draftValues)
    const draftSelectedOptionsRef = useRef<ComboboxFieldOption[]>([])
    const isOpenRef = useRef(isOpen)
    const pendingCommittedValuesRef = useRef<string[] | null>(null)

    useEffect(() => {
        isOpenRef.current = isOpen
    }, [isOpen])

    useEffect(() => {
        draftValuesRef.current = draftValues
    }, [draftValues])

    useEffect(() => {
        if (isOpen) {
            return
        }

        const pendingCommittedValues = pendingCommittedValuesRef.current
        if (pendingCommittedValues) {
            if (
                areFilterSelectorValuesEqual({
                    left: normalizedIncomingValues,
                    right: pendingCommittedValues,
                    normalizeCaseInsensitive,
                })
            ) {
                pendingCommittedValuesRef.current = null
            } else {
                return
            }
        }

        if (
            !areFilterSelectorValuesEqual({
                left: draftValuesRef.current,
                right: normalizedIncomingValues,
                normalizeCaseInsensitive,
            })
        ) {
            setDraftValues(normalizedIncomingValues)
            draftValuesRef.current = normalizedIncomingValues
        }
    }, [isOpen, normalizeCaseInsensitive, normalizedIncomingValues])

    const resolvedAsyncOptions = useMemo(
        () => (buildAsyncOptions ? buildAsyncOptions(draftValues) : asyncOptions),
        [asyncOptions, buildAsyncOptions, draftValues]
    )

    const handleValueChange = useCallback(
        (nextValue: string | string[] | null, nextOption: ComboboxFieldOption | ComboboxFieldOption[] | null) => {
            const nextValues = normalizeFilterSelectorValues({
                value: nextValue,
                multiple,
                normalizeCaseInsensitive,
            })
            const nextSelectedOptions = Array.isArray(nextOption) ? nextOption : nextOption ? [nextOption] : []

            draftValuesRef.current = nextValues
            draftSelectedOptionsRef.current = nextSelectedOptions
            setDraftValues(nextValues)
        },
        [multiple, normalizeCaseInsensitive]
    )

    const commitDraftValues = useCallback(() => {
        const nextValues = normalizeFilterSelectorValues({
            value: draftValuesRef.current,
            multiple,
            normalizeCaseInsensitive,
        })

        if (
            !areFilterSelectorValuesEqual({
                left: nextValues,
                right: normalizedIncomingValues,
                normalizeCaseInsensitive,
            })
        ) {
            pendingCommittedValuesRef.current = nextValues
            draftValuesRef.current = nextValues
            setDraftValues(nextValues)
            onChange(
                toFilterSelectorValue(nextValues, multiple),
                multiple ? draftSelectedOptionsRef.current : draftSelectedOptionsRef.current[0] || null
            )
        }
    }, [multiple, normalizeCaseInsensitive, normalizedIncomingValues, onChange])

    const handleOpenChange = useCallback(
        (open: boolean, eventDetails: ComboboxPrimitive.Root.ChangeEventDetails) => {
            if (!open && !isOpenRef.current) {
                return
            }

            setIsOpen(open, eventDetails)
            if (eventDetails.isCanceled) {
                return
            }

            isOpenRef.current = open

            if (open) {
                pendingCommittedValuesRef.current = null
                draftValuesRef.current = normalizedIncomingValues
                draftSelectedOptionsRef.current = resolveFallbackSelectedOptions({
                    values: normalizedIncomingValues,
                    availableOptions: options,
                    normalizeCaseInsensitive,
                })
                setDraftValues(normalizedIncomingValues)
                return
            }

            if (isCloseByEscape(eventDetails)) {
                pendingCommittedValuesRef.current = null
                draftValuesRef.current = normalizedIncomingValues
                draftSelectedOptionsRef.current = resolveFallbackSelectedOptions({
                    values: normalizedIncomingValues,
                    availableOptions: options,
                    normalizeCaseInsensitive,
                })
                setDraftValues(normalizedIncomingValues)
                return
            }

            commitDraftValues()
        },
        [commitDraftValues, normalizeCaseInsensitive, normalizedIncomingValues, options, setIsOpen]
    )

    useEffect(() => {
        if (!isOpen) {
            return
        }

        const closeIfIframeFocused = () => {
            if (document.activeElement instanceof HTMLIFrameElement) {
                isOpenRef.current = false
                setIsOpen(false)
                commitDraftValues()
            }
        }

        document.addEventListener('focusin', closeIfIframeFocused)
        window.addEventListener('blur', closeIfIframeFocused)

        return () => {
            document.removeEventListener('focusin', closeIfIframeFocused)
            window.removeEventListener('blur', closeIfIframeFocused)
        }
    }, [commitDraftValues, isOpen, setIsOpen])

    const handleClearValues = useCallback(() => {
        const nextValues: string[] = []

        pendingCommittedValuesRef.current = nextValues
        setDraftValues(nextValues)
        draftValuesRef.current = nextValues
        draftSelectedOptionsRef.current = []
        onChange(toFilterSelectorValue(nextValues, multiple), multiple ? [] : null)
        setIsOpen(false)
    }, [multiple, onChange, setIsOpen])

    const handleApplyValues = useCallback(() => {
        commitDraftValues()
        isOpenRef.current = false
        setIsOpen(false)
    }, [commitDraftValues, setIsOpen])

    const defaultRenderButtonValue = useCallback(
        (selectedOptions: ComboboxFieldOption[]) => {
            if (!multiple) {
                return selectedOptions[0]?.label || null
            }

            return (
                <>
                    {selectedOptions.map((selectedOption) => (
                        <Badge
                            key={selectedOption.value}
                            color="gray"
                            variant="outline"
                            className="-my-1 rounded-md px-1.5 max-md:my-0"
                        >
                            {selectedOption.label}
                        </Badge>
                    ))}
                </>
            )
        },
        [multiple]
    )

    const hasSelectedValue = multiple ? draftValues.length > 0 : !!draftValues[0]
    const hasDraftChanges = useMemo(
        () =>
            !areFilterSelectorValuesEqual({
                left: draftValues,
                right: normalizedIncomingValues,
                normalizeCaseInsensitive,
            }),
        [draftValues, normalizeCaseInsensitive, normalizedIncomingValues]
    )
    const hasCustomButtonContent = children !== undefined && children !== null
    const noValueLabel = label ?? placeholder
    const shouldRenderEmptyStateButton = !hasCustomButtonContent && !hasSelectedValue
    const resolvedRenderButtonValue = renderButtonValue || defaultRenderButtonValue
    const defaultValueClassName = multiple
        ? 'flex flex-wrap items-center gap-1 max-md:min-w-0'
        : 'min-w-0 truncate text-foreground font-medium'
    const shouldShowLabelDivider = showLabelDivider ?? multiple
    const isAsyncMode = !!(buildAsyncOptions || asyncOptions)
    const resolvedContentProps = useMemo(
        () => ({
            ...contentPropsProp,
            className: cn('flex max-h-(--available-height) min-h-0 flex-col', contentPropsProp?.className),
        }),
        [contentPropsProp]
    )
    const resolvedListProps = useMemo(
        () => ({
            ...listPropsProp,
            className: cn('min-h-0 flex-1 max-h-none overflow-y-auto', listPropsProp?.className),
        }),
        [listPropsProp]
    )

    const fallbackSelectedOptions = useMemo(
        () =>
            resolveFallbackSelectedOptions({
                values: draftValues,
                availableOptions: options,
                normalizeCaseInsensitive,
            }),
        [draftValues, normalizeCaseInsensitive, options]
    )

    const triggerComponent = presentation ? (
        <ComboboxPrimitive.Trigger render={<Button {...presentation.buttonProps} />}>
            {presentation.children}
        </ComboboxPrimitive.Trigger>
    ) : (
        <ValueSelectorTrigger
            multiple={multiple}
            isAsyncMode={isAsyncMode}
            draftValues={draftValues}
            fallbackSelectedOptions={fallbackSelectedOptions}
            resolvedRenderButtonValue={resolvedRenderButtonValue}
            shouldRenderEmptyStateButton={shouldRenderEmptyStateButton}
            hasCustomButtonContent={hasCustomButtonContent}
            noValueLabel={noValueLabel}
            label={label}
            shouldShowLabelDivider={shouldShowLabelDivider}
            defaultValueClassName={defaultValueClassName}
            buttonProps={buttonProps}
            disabled={restComboboxProps.disabled}
            showChevron={showChevron}
            invalid={invalid}
        >
            {children}
        </ValueSelectorTrigger>
    )

    return (
        <ComboboxField
            {...restComboboxProps}
            multiple={multiple}
            open={isOpen}
            value={toFilterSelectorValue(draftValues, multiple)}
            options={options}
            asyncOptions={resolvedAsyncOptions}
            onValueChange={handleValueChange}
            onOpenChange={handleOpenChange}
            queryOnOpen={queryOnOpen}
            placeholder={placeholder}
            searchPlaceholder={searchPlaceholder}
            contentProps={resolvedContentProps}
            listProps={resolvedListProps}
            triggerComponent={customTriggerComponent ?? triggerComponent}
            actions={[
                ...(actions ?? []),
                ...(clearActionLabel
                    ? [
                          {
                              key: 'apply-filter',
                              label: t('actions.applyFilter'),
                              visible: multiple && hasSelectedValue,
                              disabled: !hasDraftChanges,
                              onExecute: handleApplyValues,
                          },
                          {
                              key: 'clear-filter',
                              label: clearActionLabel,
                              visible: draftValues.length > 0,
                              onExecute: handleClearValues,
                          },
                      ]
                    : []),
            ]}
        />
    )
}
